import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { PointMm } from '~/domain/geometry/patientGeometry';
import {
    createLandmarkPairId,
    LANDMARK_NAME_MAX_LENGTH,
    LandmarkPair,
    LandmarkPoint,
} from '~/domain/landmark/landmark';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { goToPlanePoint } from '~/hooks/viewer/goToPlanePoint';
import {
    selectedLandmarkPairState,
    landmarkPairListState,
} from '~/state/atoms/landmarkAtoms';

export interface LandmarkController {
    readonly pairs: LandmarkPair[];
    readonly awaiting: 'fixed' | 'moving';
    addPoint: (point: LandmarkPoint, onMovingImage: boolean) => void;
    removePair: (id: string) => void;
    toggleVisibility: (id: string) => void;
    toggleAllVisibility: () => void;
    clearAll: () => void;
    movePoint: (id: string, half: 'fixed' | 'moving', pointMm: PointMm) => void;
    renamePair: (id: string, name: string) => void;
    recolorPair: (id: string, color: DicomColor) => void;
    readonly selectedPairId: string | null;
    togglePair: (id: string) => void;
}

function openPairIndex(pairs: readonly LandmarkPair[]): number {
    return pairs.findIndex((pair) => !pair.fixed || !pair.moving);
}

export function useLandmarkController(): LandmarkController {
    const [pairs, setPairs] = useRecoilState(landmarkPairListState);
    const [selectedPairId, setSelectedPairId] = useRecoilState(
        selectedLandmarkPairState
    );
    const viewManager = useRecoilValue(viewManagerState);

    const addPoint = useCallback(
        (point: LandmarkPoint, onMovingImage: boolean) => {
            const half = onMovingImage ? 'moving' : 'fixed';
            setPairs((prev) => {
                const index = openPairIndex(prev);
                // Re-clicking a placed half replaces it rather than starting a new pair.
                if (index >= 0) {
                    const next = [...prev];
                    next[index] = { ...prev[index], [half]: point };
                    return next;
                }
                return [
                    ...prev,
                    {
                        id: createLandmarkPairId(),
                        visible: true,
                        [half]: point,
                    },
                ];
            });
        },
        [setPairs]
    );

    const movePoint = useCallback(
        (id: string, half: 'fixed' | 'moving', pointMm: PointMm) => {
            setPairs((prev) =>
                prev.map((pair) => {
                    const point = pair[half];
                    if (pair.id !== id || !point) {
                        return pair;
                    }
                    // Plane and frame are kept: a drag never moves a point onto another scan.
                    return { ...pair, [half]: { ...point, pointMm } };
                })
            );
        },
        [setPairs]
    );

    const renamePair = useCallback(
        (id: string, name: string) => {
            const trimmed = name.trim().slice(0, LANDMARK_NAME_MAX_LENGTH);
            setPairs((prev) =>
                prev.map((pair) =>
                    pair.id === id
                        ? { ...pair, name: trimmed || undefined }
                        : pair
                )
            );
        },
        [setPairs]
    );

    const recolorPair = useCallback(
        (id: string, color: DicomColor) => {
            setPairs((prev) =>
                prev.map((pair) => (pair.id === id ? { ...pair, color } : pair))
            );
        },
        [setPairs]
    );

    const removePair = useCallback(
        (id: string) => {
            setPairs((prev) => prev.filter((pair) => pair.id !== id));
            setSelectedPairId((current) => (current === id ? null : current));
        },
        [setPairs, setSelectedPairId]
    );

    const toggleVisibility = useCallback(
        (id: string) => {
            setPairs((prev) =>
                prev.map((pair) =>
                    pair.id === id ? { ...pair, visible: !pair.visible } : pair
                )
            );
        },
        [setPairs]
    );

    const toggleAllVisibility = useCallback(() => {
        setPairs((prev) => {
            const showAll = !prev.some((pair) => pair.visible);
            return prev.map((pair) => ({ ...pair, visible: showAll }));
        });
    }, [setPairs]);

    const clearAll = useCallback(() => {
        viewManager?.interactionContext.toolManager.cancelActivePlacementDraft();
        setPairs([]);
        setSelectedPairId(null);
    }, [viewManager, setPairs, setSelectedPairId]);

    const togglePair = useCallback(
        (id: string) => {
            if (id === selectedPairId) {
                setSelectedPairId(null);
                return;
            }
            setSelectedPairId(id);
            const pair = pairs.find((candidate) => candidate.id === id);
            const point = pair?.fixed ?? pair?.moving;
            if (!point || !viewManager) {
                return;
            }
            // Each point is in its own series' frame; the go-to finds the tile that draws it.
            goToPlanePoint(viewManager, point.plane, point.pointMm);
        },
        [pairs, selectedPairId, setSelectedPairId, viewManager]
    );

    const open = openPairIndex(pairs);
    const awaiting: 'fixed' | 'moving' =
        open >= 0 && pairs[open].fixed ? 'moving' : 'fixed';

    return {
        pairs,
        awaiting,
        addPoint,
        movePoint,
        renamePair,
        recolorPair,
        removePair,
        toggleVisibility,
        toggleAllVisibility,
        clearAll,
        selectedPairId,
        togglePair,
    };
}
